import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtUSDStrict } from "@/lib/format";
import { Check, HandCoins, Loader2 } from "lucide-react";

export const DonateDialog = ({ open, onOpenChange, kind, suggested, available, charities, onDone }) => {
  const [amount, setAmount] = useState("");
  const [choice, setChoice] = useState(null);
  const [busy, setBusy] = useState(false);

  const label = kind === "zakat" ? "Zakat" : "Purification";
  const list = (charities || []).filter((c) => kind !== "zakat" || c.zakat_eligible);
  const value = Number(amount || suggested || 0);
  const tooMuch = value > (available || 0);

  const give = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/zakat/donate",
        { charity_id: choice, amount: Number(value.toFixed(2)), kind });
      toast.success(`${label} given`, { description: data.message });
      onOpenChange(false);
      setAmount(""); setChoice(null);
      onDone && onDone();
    } catch (e) {
      toast.error(`Couldn't give your ${label.toLowerCase()}`,
        { description: e?.response?.data?.detail });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="zakat-donate-dialog">
        <DialogHeader>
          <DialogTitle>Give your {label.toLowerCase()}</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {kind === "zakat"
              ? "Zakat can only go to a zakat-eligible cause. It is debited from your USD wallet."
              : "Purification is given away without counting as charity — any cause on the list can receive it."}
          </p>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label>Amount (USD)</Label>
          <Input type="number" value={amount} placeholder={Number(suggested || 0).toFixed(2)}
            onChange={(e) => setAmount(e.target.value)} data-testid="zakat-amount-input" />
          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span>Outstanding {fmtUSDStrict(suggested || 0)}</span>
            <button type="button" className="font-semibold text-foreground hover:underline"
              onClick={() => setAmount(String(Number(suggested || 0).toFixed(2)))}
              data-testid="zakat-amount-full">Give it all</button>
          </div>
          {tooMuch && (
            <p className="text-xs font-medium text-destructive" data-testid="zakat-amount-error">
              Your USD wallet holds {fmtUSDStrict(available || 0)}.
            </p>
          )}
        </div>

        <div className="space-y-2 max-h-64 overflow-auto pe-1">
          {list.map((c) => (
            <button key={c.id} type="button" onClick={() => setChoice(c.id)}
              data-testid={`zakat-charity-${c.id}`}
              className={`w-full text-start rounded-xl border p-3 transition-colors ${
                choice === c.id ? "border-[color:var(--tharwy-lime)] bg-secondary" : "hover:bg-secondary/60"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-sm">{c.name}</span>
                {choice === c.id && <Check className="h-4 w-4 text-[color:var(--tharwy-lime)]" />}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">{c.cause} · {c.region}</div>
              <div className="text-[11px] text-muted-foreground mt-1 leading-relaxed">{c.blurb}</div>
            </button>
          ))}
        </div>

        <p className="text-[11px] text-muted-foreground leading-relaxed">
          SIMULATED: your wallet is debited and a certificate is issued inside Tharwy — no funds
          leave the platform yet.
        </p>

        <DialogFooter>
          <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
            disabled={busy || !choice || value <= 0 || tooMuch} onClick={give}
            data-testid="zakat-donate-confirm">
            {busy ? <Loader2 className="h-4 w-4 me-1.5 animate-spin" /> : <HandCoins className="h-4 w-4 me-1.5" />}
            Give {fmtUSDStrict(value)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
